import { Component, Input, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/operators';
import { CustomerBillerStoreService } from '../../../billers/services/customer-biller-store.service';
import { XsellService } from '../../../billers/services/xsell.service';
import { AnalyticsEvent, AnalyticsService } from '../../../core/services/analytics-service';
import { AppConfig } from '../../../core/services/app-config.service';
import { DialogService } from '../../../core/services/dialog.service';
import { DemoService } from '../../../demo/demo.service';
import { OFFER_DIALOG_MAX_WIDTH, OFFER_DIALOG_WIDTH } from '../../../shared/configs/dialog-config';
import { ILinkOfferPayload, IOffer } from '../../models/model';
import { OfferType } from '../../models/offer-type.enum';
import { OffersStoreService } from '../../services/offers-store.service';
import { DobryPradOfferDialogComponent } from '../dobry-prad-offer-dialog/dobry-prad-offer-dialog.component';
import { OfferDetailsDialogComponent } from '../offer-details/offer-details-dialog.component';

@Component({
standalone: false,
  selector: 'app-offer-tile',
  styleUrls: ['./offer-tile.component.scss'],
  templateUrl: './offer-tile.component.html',
})
export class OfferTileComponent implements OnChanges {
  @Input() public offer: IOffer;
  public hover: boolean;

  constructor(
    private analyticsService: AnalyticsService,
    private appConfig: AppConfig,
    private dialogService: DialogService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private demoService: DemoService,
    private offersStoreService: OffersStoreService,
    private matDialog: MatDialog,
    private xsellService: XsellService,
    private customerBillerStore: CustomerBillerStoreService,
  ) {}

  public ngOnChanges(): void {
    if (this.offer) {
      this.analyticsService.trackEvent(
        new AnalyticsEvent('Offer Card Displayed', { offerId: this.offer.offerId, title: this.offer.title }),
      );
    }
  }

  public showDetails(): void {
    if (!this.appConfig.config.demo) {
      this.trackOfferSeenEvent();
      if (this.offer.type === OfferType.MGM) {
        this.offersStoreService
          .getOffer(this.offer.offerId)
          .subscribe(() => this.router.navigate([(this.offer.payload as ILinkOfferPayload).linkUrl]));
      } else if (this.offer.type === OfferType.DOBRY_PRAD) {
        this.openDobryPradOffer();
      } else if (this.offer.type === OfferType.PHONE) {
        this.openPhoneOffer();
      } else {
        this.openAnyOffer();
      }
    } else {
      this.demoService.openDialog();
    }
  }

  private trackOfferSeenEvent(): void {
    this.analyticsService.trackEvent(
      new AnalyticsEvent('Offer seen', {
        offerId: this.offer.offerId,
        offerTitle: this.offer.title,
        offerType: this.offer.type,
      }),
    );
  }

  openOfferDialog(dialogComponent, config) {
    this.getOffer()
      .pipe(
        tap(() =>
          setTimeout(() => {
            if (!this.isAnyDialogOpened()) {
              this.dialogService.open(dialogComponent, config);
            }
          }, 0),
        ),
      )
      .subscribe(() => this.markAsSeen());
  }

  private openDobryPradOffer(): void {
    this.openOfferDialog(DobryPradOfferDialogComponent, {
      maxWidth: OFFER_DIALOG_MAX_WIDTH,
      width: OFFER_DIALOG_WIDTH,
    });
  }

  private openAnyOffer(): void {
    this.openOfferDialog(OfferDetailsDialogComponent, {
      data: this.offer,
      maxWidth: this.offer.type === 'ORANGE' ? '100%' : '80%',
      width: OFFER_DIALOG_WIDTH,
    });
  }

  private openPhoneOffer(): void {
    const availableXsellCustomerBillerId = this.customerBillerStore.state
      ? this.customerBillerStore.state.filter((cb) => cb.showXsellConsentBtn).map((cb) => cb.id)[0]
      : null;
    this.xsellService
      .showXsellConsentAdditionalInfo(availableXsellCustomerBillerId, this.activatedRoute, 'offer')
      .subscribe();
  }

  private getOffer(): Observable<IOffer> {
    return this.offersStoreService.getOffer(this.offer.offerId);
  }

  private markAsSeen(): void {
    this.offer.seen = true;
  }

  private isAnyDialogOpened(): boolean {
    return this.matDialog.openDialogs.length > 0;
  }
}
